import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import ShoppingBagIcon from "@redotech/redo-icons/shopping-bag-01.svg";
import ShoppingBagFilledIcon from "@redotech/redo-icons/shopping-bag-01_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TAB_PARAM } from "@redotech/redo-merchant-fulfillment-app/outbound-order";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  ORDERS_ICON,
  ORDERS_ICON_FILLED,
} from "../../../../redo-suite/order-management-cloud";
import { OrderEditsItem } from "./order-edits";

// Sentinel pathRoot used to opt the Orders primary tab out of the
// `selectedPrimaryTab` substring match when the user arrived at an order
// from a fulfillment-group view. Contains a character that cannot legally
// appear in a URL pathname, so `location.pathname.includes(...)` is always
// false. The Shipping tab then wins the `find()` and the left nav stays in
// the Shipping/Fulfillments context.
const ORDERS_PATH_ROOT_INACTIVE = "\0";

export function useOrdersPrimaryNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const [searchParams] = useSearchParams();

  const canViewOrders = useHasPermission(OrderManagementPermissions.VIEW_ORDER);

  if (!canViewOrders) {
    return null;
  }

  const href = routes.getOrdersOutboundLabelsSubRoute("orders");
  // `/outbound-labels/order` covers both `/outbound-labels/orders/*` and
  // `/outbound-labels/order-edits`; narrower than the Shipping tab's
  // `/shipping` pathRoot so `find()` picks Orders first when the user is
  // legitimately browsing the Orders area.
  //
  // OMS-3071: when the URL carries the `outboundOrderTab` param the user
  // opened this order from a fulfillment-group view (Ready to ship, Priority,
  // etc.) and we want the left nav to stay in the Shipping → Fulfillments
  // context so they can hop between fulfillment views without re-navigating.
  // Swap to the sentinel pathRoot so the Shipping tab wins instead.
  const cameFromFulfillmentView = searchParams.has(TAB_PARAM);
  const pathRoot = cameFromFulfillmentView
    ? ORDERS_PATH_ROOT_INACTIVE
    : `${routes.ordersOutboundLabelsHref}/order`;

  const primaryTab: LinkPrimaryTab = {
    id: "orders",
    hasNotification: false,
    UnfilledIcon: ShoppingBagIcon,
    FilledIcon: ShoppingBagFilledIcon,
    type: "link",
    pathRoot,
    href,
    friendlyName: "Orders",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "orders",
    name: "Orders",
    NavContent: OrdersAreaSecondaryNavigation,
  };

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: true,
    isCore: true,
  };
}

const ORDER_VIEWS: { key: string; name: string }[] = [
  { key: "all", name: "All" },
  { key: "protected", name: "Covered" },
  { key: "fulfilled", name: "Fulfilled" },
  { key: "partially_fulfilled", name: "Partially fulfilled" },
  { key: "not_fulfilled", name: "Unfulfilled" },
];

const OrdersAreaSecondaryNavigation = memo(
  function OrdersAreaSecondaryNavigation() {
    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: "Orders",
    });

    return (
      <TreeContext.Provider value={nodeId}>
        {/* Orders has no dashboard; the orders list is its landing page. */}
        <OrdersWithViewsItem />
        <OrderEditsItem />
      </TreeContext.Provider>
    );
  },
);

const OrdersWithViewsItem = memo(function OrdersWithViewsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const ordersListPath = routes.getOrdersOutboundLabelsSubRoute("orders");
  const isOnOrdersArea = location.pathname.startsWith(ordersListPath);
  const isOnOrdersListPage =
    location.pathname === ordersListPath ||
    location.pathname === `${ordersListPath}/`;
  const currentFilter = searchParams.get("filter") ?? "all";

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isOnOrdersArea}
      FilledIcon={ORDERS_ICON_FILLED}
      friendlyName="Orders"
      id="orders"
      isFolder
      UnfilledIcon={ORDERS_ICON}
    >
      {ORDER_VIEWS.map((view) => (
        <OrdersFilterViewItem
          href={
            view.key === "all"
              ? ordersListPath
              : `${ordersListPath}?filter=${view.key}`
          }
          key={view.key}
          name={view.name}
          selected={isOnOrdersListPage && currentFilter === view.key}
          viewKey={view.key}
        />
      ))}
    </RedoSecondaryNavItem>
  );
});

const OrdersFilterViewItem = memo(function OrdersFilterViewItem({
  href,
  name,
  selected,
  viewKey,
}: {
  href: string;
  name: string;
  selected: boolean;
  viewKey: string;
}) {
  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      friendlyName={name}
      id={`orders-view-${viewKey}`}
      selected={selected}
    />
  );
});
